import {
  MODIFY_DETAILS_METER_RATES,
  PUBLISH_CONFIG_OPTIONS,
  TIER_IDS,
  formatCreditDecimal,
  formatEuroPerCredit,
  parseCreditNumber,
  parseEuroNumber,
} from './mockData';

export const PUBLISH_SCOPE_OPTIONS = [
  { value: 'New Contract', label: 'New contracts only' },
  { value: 'New + Renewal', label: 'New + renewing' },
  { value: 'All', label: 'All from next period' },
];

const TIER_CREDIT_FIELD = {
  S: 'credits_small',
  M: 'credits_medium',
  L: 'credits_large',
};

export function unwrapApiData(response) {
  const payload = response?.data;
  if (payload == null) return null;
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (payload.data != null) return payload.data;
  return payload;
}

export function formatEuroDisplay(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const [whole, frac = '00'] = n.toFixed(2).split('.');
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return frac === '00' ? grouped : `${grouped},${frac}`;
}

export function formatCreditsDisplay(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  if (Number.isInteger(n)) return String(n);
  return formatCreditDecimal(n);
}

export function meterRateRowId(rate) {
  const fromTrigger = String(rate?.trigger || '')
    .replace(/^\//, '')
    .replace(/[^a-zA-Z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  if (fromTrigger) return fromTrigger;
  return String(rate?.action || 'rate')
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+/g, '-');
}

function mapPlanRow(tierId, plan = {}) {
  return {
    id: tierId,
    planId: plan.id != null ? Number(plan.id) : null,
    description: plan.description || '',
    packageMo: formatEuroDisplay(plan.price ?? ''),
    includedCredits: formatCreditsDisplay(plan.credits ?? ''),
    modelIds: Array.isArray(plan.models) ? plan.models.map(Number) : [],
    type: plan.type || 'monthly',
    editsId: plan.edits_id ?? null,
    effectiveFrom: plan.effective_from ?? null,
  };
}

export function mapPlansFromApi(plans = []) {
  const byTier = Object.fromEntries(
    (Array.isArray(plans) ? plans : []).map((plan) => [plan.tier, plan])
  );

  return TIER_IDS.map((tierId) => mapPlanRow(tierId, byTier[tierId]));
}

export function mapTrialPlanFromApi(plans = []) {
  const raw = (Array.isArray(plans) ? plans : []).find((plan) => plan.tier === 'T');
  return raw ? mapPlanRow('T', raw) : null;
}

export function mapUnlimitedPlanFromApi(plans = []) {
  const raw = (Array.isArray(plans) ? plans : []).find((plan) => plan.tier === 'U');
  return raw ? mapPlanRow('U', raw) : null;
}

export function mapModelsFromApi(models = []) {
  return (Array.isArray(models) ? models : []).map((model) => ({
    id: Number(model.id),
    name: model.name || model.provider || model.label || `Model ${model.id}`,
    provider: model.provider || model.name || model.label || `Model ${model.id}`,
  }));
}

export function buildModelToggles(models, planTiers) {
  const toggles = {};
  models.forEach((model) => {
    toggles[model.provider] = Object.fromEntries(
      TIER_IDS.map((tierId) => {
        const tier = planTiers.find((t) => t.id === tierId);
        const enabled = tier?.modelIds?.includes(model.id) ?? false;
        return [tierId, enabled];
      })
    );
  });
  return toggles;
}

export function mapMeterRatesFromApi(rates = []) {
  const rows = (Array.isArray(rates) ? rates : []).map((rate) => ({
    id: meterRateRowId(rate),
    action: rate.action || '',
    trigger: rate.trigger || '',
  }));

  const values = {};
  rows.forEach((row, index) => {
    const source = (Array.isArray(rates) ? rates : [])[index] || {};
    values[row.id] = Object.fromEntries(
      TIER_IDS.map((tierId) => {
        const field = TIER_CREDIT_FIELD[tierId];
        return [tierId, formatCreditsDisplay(source[field] ?? '')];
      })
    );
  });

  // All rows of one response come from the same version (null = base rows).
  const editsId = (Array.isArray(rates) ? rates : [])[0]?.edits_id ?? null;

  return { rows, values, editsId };
}

export function mapEditHistoryFromApi(history = []) {
  return (Array.isArray(history) ? history : []).map((row, index) => ({
    id: row.id,
    version: row.version || row.id || `v${index + 1}`,
    status: row.status || '—',
    changedBy:
      row.edited_by_user?.full_name ||
      row.changed_by ||
      row.changedBy ||
      row.user ||
      '—',
    note: row.description || row.note || '—',
    date: row.effective_from
      ? formatEffectiveFromDisplay(row.effective_from)
      : row.date || '—',
    createdAt: row.created_at
      ? formatEffectiveFromDisplay(row.created_at)
      : '—',
  }));
}

const CYCLE_LABELS = {
  monthly: 'Monthly',
  yearly: 'Yearly',
  trial: 'Trial',
  unlimited: 'Unlimited',
};

const TIER_SELECT_OPTIONS = [
  { value: 'S', label: 'S (Starter)' },
  { value: 'M', label: 'M (Mid)' },
  { value: 'L', label: 'L (Large)' },
];

const CYCLE_SELECT_OPTIONS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'yearly', label: 'Yearly (-10%)' },
];

export function formatCycleLabel(cycle) {
  return CYCLE_LABELS[String(cycle || '').toLowerCase()] || cycle || '—';
}

export function mapContractFromApi(contract = {}) {
  const tier = contract.tier || 'S';
  const cycle = String(contract.cycle || 'monthly').toLowerCase();

  return {
    id: contract.id,
    agencyId: contract.agency_id ?? contract.agency?.id,
    name: contract.agency?.name || contract.agency_name || '—',
    tier,
    tierNote: contract.has_custom_credits ? 'custom credits' : null,
    cycle: formatCycleLabel(cycle),
    cycleValue: cycle,
    cycleOverride: Boolean(contract.has_cycle_override),
    autoRenew: contract.auto_renew !== false,
    creditsUsed: contract.used_credits ?? 0,
    creditsIncluded: contract.max_credits ?? 0,
    capCredits: contract.cap_credits ?? null,
    planCredits: contract.plan_credits ?? null,
    startDate: contract.start_date,
    endDate: contract.end_date,
    billingEmail: contract.billing_email ?? '',
    // INV-10: closed and deleted-agency rows (status filter) are read-only.
    isActive: contract.is_active !== false,
    closedReason: contract.closed_reason ?? null,
    agencyDeleted: Boolean(contract.agency?.deleted_at),
    // Closed list only: a PO-paused agency can be restarted once this is set.
    poNumber: contract.agency?.po_number ?? '',
    overrides: {
      tier,
      includedCredits: String(contract.max_credits ?? ''),
      billingCycle: cycle,
      billingEmail: contract.billing_email ?? '',
    },
    grants: [],
  };
}

export function mapContractsFromApi(response) {
  const rows = Array.isArray(response?.data) ? response.data : [];
  return {
    contracts: rows.map(mapContractFromApi),
    pagination: response?.pagination || null,
  };
}

export function mapContractAdjustmentsFromApi(history = []) {
  return (Array.isArray(history) ? history : []).map((row) => ({
    id: row.id,
    type: row.type || 'Grant',
    credits: row.credits ?? '—',
    reason: row.reason?.trim() ? row.reason : '—',
    by:
      row.changed_by_user?.full_name ||
      row.by ||
      row.changedBy ||
      '—',
    changedAt: row.changed_at,
  }));
}

export function buildOverrideContractPayload(contractId, field, value) {
  const payload = { contract_id: Number(contractId) };

  if (field === 'useListPrice') {
    payload.use_list_price = true;
  } else if (field === 'tier') {
    payload.tier = value;
  } else if (field === 'billingCycle') {
    payload.cycle = value;
  } else if (field === 'includedCredits') {
    const credits = parseCreditNumber(value);
    if (credits >= 0) payload.max_credits = Math.round(credits);
  } else if (field === 'billingEmail') {
    payload.billing_email = value?.trim() || '';
  }

  return payload;
}

export function buildCreateContractPayload({
  agencyId,
  planId,
  startDate,
  capCredits,
  billingEmail,
  override,
  cycle,
}) {
  const payload = {
    agency_id: Number(agencyId),
    plan_id: Number(planId),
    start_date: startDate,
    billing_email: billingEmail?.trim() || '',
  };

  if (cycle === 'monthly' || cycle === 'yearly') {
    payload.cycle = cycle;
  }

  if (capCredits !== '' && capCredits != null) {
    payload.cap_credits = Math.round(parseCreditNumber(capCredits));
  }
  if (override) {
    payload.override = true;
  }

  return payload;
}

export function buildToggleRenewalPayload(contractId, autoRenew) {
  return {
    contract_id: Number(contractId),
    auto_renew: Boolean(autoRenew),
  };
}

export function buildAdjustmentPayload({ contractId, type, credits, reason }) {
  return {
    contract_id: Number(contractId),
    type,
    credits: Number(credits),
    reason: reason?.trim() || undefined,
  };
}

export { TIER_SELECT_OPTIONS, CYCLE_SELECT_OPTIONS };

export function getEnabledModelIds(modelToggles, tierId, models) {
  return models
    .filter((model) => modelToggles[model.provider]?.[tierId])
    .map((model) => model.id);
}

function findMeterRateRow(meterRateRows, trigger) {
  return (meterRateRows || []).find((row) => row.trigger === trigger);
}

function buildPlansPayload({ planTiers, modelToggles, models }) {
  return TIER_IDS.map((tierId) => {
    const tier = (planTiers || []).find((item) => item.id === tierId) || { id: tierId };
    return {
      tier: tierId,
      description: tier.description || '',
      price: parseEuroNumber(tier.packageMo),
      type: tier.type || 'monthly',
      credits: parseCreditNumber(tier.includedCredits),
      models: getEnabledModelIds(modelToggles, tierId, models),
    };
  });
}

function buildRatesPayload({ meterRateRows, meterRates }) {
  return MODIFY_DETAILS_METER_RATES.map((canonical) => {
    const row =
      findMeterRateRow(meterRateRows, canonical.trigger) || {
        id: canonical.id || meterRateRowId(canonical),
        action: canonical.action,
        trigger: canonical.trigger,
      };
    const credits = meterRates?.[row.id] || {};

    return {
      action: row.action || canonical.action,
      trigger: canonical.trigger,
      credits_small: parseCreditNumber(credits.S),
      credits_medium: parseCreditNumber(credits.M),
      credits_large: parseCreditNumber(credits.L),
    };
  });
}

export function mapPublishConfigToSections(selectedConfig = []) {
  const sections = new Set();
  selectedConfig.forEach((id) => {
    const option = PUBLISH_CONFIG_OPTIONS.find((item) => item.id === id);
    if (option?.section) sections.add(option.section);
  });
  return Array.from(sections);
}

export function buildModifyDetailsPayload({
  description,
  effectiveFrom,
  effectiveFor,
  publishConfig,
  planTiers,
  modelToggles,
  models,
  meterRateRows,
  meterRates,
}) {
  const section = mapPublishConfigToSections(publishConfig);

  return {
    description: description?.trim() || 'Rate card update',
    effective_from: formatEffectiveFromForApi(effectiveFrom),
    section,
    effective_for: effectiveFor,
    plans: buildPlansPayload({ planTiers, modelToggles, models }),
    rates: buildRatesPayload({ meterRateRows, meterRates }),
  };
}

export function formatEffectiveFromForApi(input) {
  const raw = String(input || '').trim();
  const dmY = raw.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  let date;

  if (dmY) {
    const [, day, month, year] = dmY;
    date = new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);
  } else if (iso) {
    const [, year, month, day] = iso;
    date = new Date(Number(year), Number(month) - 1, Number(day), 0, 0, 0, 0);
  } else if (raw) {
    date = new Date(raw);
  } else {
    date = new Date();
  }

  if (Number.isNaN(date.getTime())) {
    date = new Date();
  }

  const pad = (n, width = 2) => String(n).padStart(width, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}.${pad(date.getMilliseconds(), 2)}`;
}

export function effectiveDisplayToIso(display) {
  const dmY = String(display || '').match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
  if (!dmY) return '';
  const [, day, month, year] = dmY;
  return `${year}-${month}-${day}`;
}

export function isoToEffectiveDisplay(iso) {
  const match = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return '';
  const [, year, month, day] = match;
  return `${day}.${month}.${year}`;
}

export function formatEffectiveFromDisplay(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export function countModelsEnabled(modelToggles, tierId, models) {
  return models.filter((model) => modelToggles[model.provider]?.[tierId]).length;
}

export function computeTierCapacity({
  tierId,
  planTier,
  meterRateRows,
  meterRates,
  modelToggles,
  models,
}) {
  const included = parseCreditNumber(planTier.includedCredits);
  const modelsOn = countModelsEnabled(modelToggles, tierId, models);
  const totalModels = models.length;

  const actions = {};
  meterRateRows.forEach((row) => {
    const rate = parseCreditNumber(meterRates[row.id]?.[tierId]);
    const key = row.id;
    const cost =
      row.action?.toLowerCase().includes('simulation') && modelsOn > 0
        ? rate * modelsOn
        : rate;
    actions[key] = cost > 0 ? Math.floor(included / cost) : null;
  });

  const simulationRow = meterRateRows.find((row) =>
    row.action?.toLowerCase().includes('simulation')
  );
  const simRate = simulationRow
    ? parseCreditNumber(meterRates[simulationRow.id]?.[tierId])
    : 0;
  const simCost = simRate * modelsOn;

  return {
    included: Math.round(included),
    actions,
    meterRateRows,
    modelsLabel:
      totalModels === 0
        ? '—'
        : modelsOn === totalModels
          ? `all ${totalModels}`
          : `${modelsOn} of ${totalModels}`,
    perCredit: formatEuroPerCredit(planTier.packageMo, planTier.includedCredits),
    note:
      totalModels === 0
        ? '—'
        : `${modelsOn} of ${totalModels} models active · ${formatCreditDecimal(simCost)} cr / full simulation`,
  };
}
